import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchCommissions, fetchCommissionSummary,
  createCommission, updateCommission, deleteCommission,
  fetchCoordinators,
} from "./commissionService";
import { fetchAllCourses, fetchBatches } from "../../courses/adminCourseService";
import api from "../../services/admin_api_global";
import PhoneInput from "../../../common/components/PhoneInput";
import axios from "axios";
import "../payments/paymentDashboard.css";

// ── User Search Input ─────────────────────────────────────────
const UserSearchInput = ({ value, onChange }) => {
  const [query,   setQuery]   = useState(value || "");
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    setOpen(false);
    if (!q.trim()) { onChange(null); setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setLoading(true);
      api.get("/admin/users", { params: { page: 0, size: 8, search: q } })
        .then((res) => {
          const list = res.data?.data || res.data?.content || (Array.isArray(res.data) ? res.data : []);
          setResults(list);
          setOpen(true);
        })
        .catch(() => { setResults([]); setOpen(true); })
        .finally(() => setLoading(false));
    }, 400);
  };

  const select = (u) => {
    setQuery(`${u.name} (${u.email || `#${u.id}`})`);
    setOpen(false);
    setResults([]);
    onChange(u);
  };

  const clear = () => { setQuery(""); setOpen(false); setResults([]); onChange(null); };

  return (
    <div style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <input value={query} onChange={handleChange} placeholder="Search by name or email…"
          style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: "0.875rem", outline: "none", boxSizing: "border-box", paddingRight: query ? 28 : 10 }} />
        {loading && <div style={{ position: "absolute", right: 10, top: 9, fontSize: "0.75rem", color: "#94a3b8" }}>…</div>}
        {query && !loading && (
          <div onMouseDown={clear} style={{ position: "absolute", right: 8, top: 7, cursor: "pointer", color: "#94a3b8", fontSize: "1rem", lineHeight: 1 }}>✕</div>
        )}
      </div>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 200, maxHeight: 220, overflowY: "auto" }}>
          {results.length === 0
            ? <div style={{ padding: "10px 12px", fontSize: "0.82rem", color: "#94a3b8" }}>No users found.</div>
            : results.map((u) => (
              <div key={u.id} onMouseDown={() => select(u)}
                style={{ padding: "8px 12px", cursor: "pointer", fontSize: "0.82rem", borderBottom: "1px solid #f1f5f9" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}>
                <span style={{ fontWeight: 600 }}>{u.name}</span>
                {u.email && <span style={{ color: "#6b7280", marginLeft: 8 }}>{u.email}</span>}
                <span style={{ color: "#94a3b8", marginLeft: 8, fontSize: "0.75rem" }}>#{u.id}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

// ── Constants ─────────────────────────────────────────────────
const STATUSES  = ["INITIATED", "PENDING", "PAID", "FAILED", "CANCELLED"];
const DEAL_TYPES = ["SUBSCRIPTION", "COURSE"];

const STATUS_BADGE = {
  INITIATED:  "pd-badge-gray",
  PENDING:    "pd-badge-amber",
  PAID:       "pd-badge-green",
  FAILED:     "pd-badge-red",
  CANCELLED:  "pd-badge-gray",
};

const TABS = ["Commissions", "Summary"];

// ── Helpers ───────────────────────────────────────────────────
const fmtAmt  = (v) => v != null ? `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const calcCommission = (price, pct) => {
  const p = parseFloat(price), c = parseFloat(pct);
  if (!p || !c) return 0;
  return Math.round((p * c / 100) * 100) / 100;
};

const emptyForm = () => ({
  coordinatorId: "", dealType: "SUBSCRIPTION",
  subscriptionPlanId: "", courseId: "", batchId: "",
  buyerUserId: "", buyerName: "", buyerPhone: "", buyerCountryCode: "+91",
  originalPrice: "", discountedPrice: "", commissionPercent: "",
  status: "INITIATED", notes: "",
});

// ── Toast ─────────────────────────────────────────────────────
const Toast = ({ msg, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return <div className={`pd-toast ${type === "error" ? "pd-toast-error" : "pd-toast-success"}`}>{msg}</div>;
};

// ── Confirm Dialog ────────────────────────────────────────────
const ConfirmDialog = ({ message, onConfirm, onCancel, danger }) => (
  <div className="pd-overlay">
    <div className="pd-modal pd-modal-sm">
      <p className="pd-confirm-msg">{message}</p>
      <div className="pd-modal-actions">
        <button className={`pd-btn ${danger ? "pd-btn-danger" : "pd-btn-primary"}`} onClick={onConfirm}>Confirm</button>
        <button className="pd-btn pd-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  </div>
);

// ── Summary Tab ───────────────────────────────────────────────
const SummaryTab = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommissionSummary().then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="pd-loading">Loading summary…</div>;
  if (!data)   return <div className="pd-empty">No summary data.</div>;

  const statCards = [
    { label: "Initiated",  value: data.totalInitiated, cls: "pd-stat-card" },
    { label: "Pending",    value: data.totalPending,   cls: "pd-stat-card pd-stat-pending" },
    { label: "Paid",       value: data.totalPaid,      cls: "pd-stat-card pd-stat-success" },
    { label: "Failed",     value: data.totalFailed,    cls: "pd-stat-card pd-stat-failed" },
    { label: "Cancelled",  value: data.totalCancelled, cls: "pd-stat-card" },
  ];

  return (
    <div style={{ padding: "1rem 1.5rem" }}>
      <div className="pd-summary-strip" style={{ padding: 0, marginBottom: "1.5rem" }}>
        {statCards.map((c) => (
          <div key={c.label} className={c.cls}>
            <div className="pd-stat-label">{c.label}</div>
            <div className="pd-stat-value">{fmtAmt(c.value)}</div>
          </div>
        ))}
      </div>

      {data.byCoordinator?.length > 0 && (
        <>
          <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#374151", marginBottom: "0.75rem" }}>By Coordinator</div>
          <div className="pd-table-wrap">
            <table className="pd-table">
              <thead>
                <tr>
                  <th>Coordinator</th>
                  <th>Total Earned</th>
                  <th>Total Paid</th>
                  <th>Total Pending</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {data.byCoordinator.map((c) => (
                  <tr key={c.coordinatorId}>
                    <td className="pd-cell-bold">{c.coordinatorName}</td>
                    <td className="pd-cell-amount">{fmtAmt(c.totalEarned)}</td>
                    <td><span className="pd-badge pd-badge-green">{fmtAmt(c.totalPaid)}</span></td>
                    <td><span className="pd-badge pd-badge-amber">{fmtAmt(c.totalPending)}</span></td>
                    <td className="pd-cell-sub">{c.commissionCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ── Add / Edit Modal ──────────────────────────────────────────
const CommissionModal = ({ initial, coordinators, onClose, onSuccess }) => {
  const [form,    setForm]    = useState(initial ? {
    coordinatorId:      initial.coordinatorId      || "",
    dealType:           initial.dealType           || "SUBSCRIPTION",
    subscriptionPlanId: initial.subscriptionPlanId || "",
    courseId:           initial.courseId           || "",
    batchId:            initial.batchId            || "",
    buyerUserId:        initial.buyerUserId        || "",
    buyerName:          initial.buyerName          || "",
    buyerPhone:         initial.buyerPhone         || "",
    buyerCountryCode:   initial.buyerCountryCode   || "+91",
    originalPrice:      initial.originalPrice      || "",
    discountedPrice:    initial.discountedPrice    || "",
    commissionPercent:  initial.commissionPercent  || "",
    status:             initial.status             || "INITIATED",
    notes:              initial.notes              || "",
  } : emptyForm());

  const [plans,   setPlans]   = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [countryCodes, setCountryCodes] = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState("");
  const [confirm, setConfirm] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleBuyerSelect = (u) => {
    if (!u) { setForm((f) => ({ ...f, buyerUserId: "", buyerName: "", buyerPhone: "", buyerCountryCode: "+91" })); return; }
    setForm((f) => ({
      ...f,
      buyerUserId:      u.id,
      buyerName:        u.name        || f.buyerName,
      buyerPhone:       u.mobileNumber || f.buyerPhone,
      buyerCountryCode: u.countryCode  || f.buyerCountryCode,
    }));
  };

  const liveCommission = calcCommission(form.discountedPrice, form.commissionPercent);
  const isPaidWarning  = form.status === "PAID" && initial?.status !== "PAID";
  const isUnpaidWarning = initial?.status === "PAID" && form.status === "CANCELLED";

  // Load country codes
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BASE_URL}/public/country-codes`)
      .then((res) => setCountryCodes(res.data?.data || []))
      .catch(() => {});
  }, []);

  // Load plans via admin_api_global (fresh token) — filter active only
  useEffect(() => {
    api.get("/admin/subscription-plans/all_plans")
      .then((res) => {
        const all  = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const list = all.filter((p) => p.isActive);
        setPlans(list);
      })
      .catch(() => {});
  }, []);

  // Load courses
  useEffect(() => {
    fetchAllCourses().then((r) => setCourses(r.data || [])).catch(() => {});
  }, []);

  // Load batches when course changes
  useEffect(() => {
    if (form.dealType === "COURSE" && form.courseId) {
      fetchBatches(form.courseId).then((r) => setBatches(r.data || [])).catch(() => setBatches([]));
    } else {
      setBatches([]);
    }
  }, [form.courseId, form.dealType]);

  // Auto-fill originalPrice + discountedPrice from plan
  const handlePlanChange = (planId) => {
    set("subscriptionPlanId", planId);
    const plan = plans.find((p) => String(p.id) === String(planId));
    if (plan) setForm((f) => ({ ...f, originalPrice: plan.price || "", discountedPrice: plan.price || "" }));
  };

  // Auto-fill originalPrice + discountedPrice from course
  const handleCourseChange = (courseId) => {
    set("courseId", courseId);
    set("batchId", "");
    const course = courses.find((c) => String(c.id) === String(courseId));
    if (course) {
      const raw = course.priceBatch || course.pricePersonal || "";
      const price = raw ? raw / 100 : "";
      setForm((f) => ({ ...f, courseId, batchId: "", originalPrice: price, discountedPrice: price }));
    }
  };

  const validate = () => {
    if (!form.coordinatorId)                          return "Coordinator is required.";
    if (!form.dealType)                               return "Deal type is required.";
    if (form.dealType === "SUBSCRIPTION" && !form.subscriptionPlanId) return "Subscription plan is required.";
    if (form.dealType === "COURSE"       && !form.courseId)           return "Course is required.";
    if (!form.originalPrice   || Number(form.originalPrice)   <= 0)  return "Original price must be positive.";
    const discounted = form.discountedPrice || form.originalPrice;
    if (!discounted || Number(discounted) <= 0)  return "Discounted price must be positive.";
    if (!form.commissionPercent || Number(form.commissionPercent) <= 0 || Number(form.commissionPercent) > 100)
      return "Commission % must be between 0.01 and 100.";
    return null;
  };

  const doSave = async () => {
    setSaving(true); setErr("");
    try {
      const body = {
        coordinatorId:     Number(form.coordinatorId),
        dealType:          form.dealType,
        originalPrice:     Number(form.originalPrice),
        discountedPrice:   Number(form.discountedPrice || form.originalPrice),
        commissionPercent: Number(form.commissionPercent),
        commissionAmount:  liveCommission,
        status:            form.status,
        ...(form.dealType === "SUBSCRIPTION" && { subscriptionPlanId: Number(form.subscriptionPlanId) }),
        ...(form.dealType === "COURSE"       && { courseId: Number(form.courseId) }),
        ...(form.batchId    && { batchId: Number(form.batchId) }),
        ...(form.buyerUserId && { buyerUserId: Number(form.buyerUserId) }),
        ...(form.buyerName   && { buyerName: form.buyerName.trim() }),
        ...(form.buyerPhone  && { buyerPhone: form.buyerPhone.trim(), buyerCountryCode: form.buyerCountryCode }),
        ...(form.notes       && { notes: form.notes.trim() }),
      };
      if (initial?.id) await updateCommission(initial.id, body);
      else             await createCommission(body);
      onSuccess(initial?.id ? "Commission updated." : "Commission added.");
    } catch (e) { setErr(e?.response?.data?.message || "Failed to save."); }
    finally { setSaving(false); setConfirm(null); }
  };

  const save = () => {
    const vErr = validate();
    if (vErr) { setErr(vErr); return; }

    if (isPaidWarning) {
      setConfirm({
        message: `Marking as PAID will auto-create an expense entry of ${fmtAmt(liveCommission)} under COMMISSION category. Proceed?`,
        onConfirm: doSave,
      });
      return;
    }
    if (isUnpaidWarning) {
      setConfirm({
        message: "This will void the linked expense entry. Proceed?",
        danger: true,
        onConfirm: doSave,
      });
      return;
    }
    doSave();
  };

  return (
    <div className="pd-overlay">
      {confirm && <ConfirmDialog message={confirm.message} danger={confirm.danger} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      <div className="pd-modal" style={{ maxWidth: 560 }}>
        <h3 className="pd-modal-title">{initial?.id ? "Edit Commission" : "Add Commission"}</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1rem" }}>
          {/* Coordinator */}
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Coordinator *</label>
            <select value={form.coordinatorId} onChange={(e) => set("coordinatorId", e.target.value)}>
              <option value="">— Select coordinator —</option>
              {coordinators.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.coordinatorType})</option>
              ))}
            </select>
          </div>

          {/* Deal Type */}
          <div className="pd-form-group">
            <label>Deal Type *</label>
            <select value={form.dealType} onChange={(e) => { set("dealType", e.target.value); set("subscriptionPlanId", ""); set("courseId", ""); set("batchId", ""); set("originalPrice", ""); }}>
              {DEAL_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {/* Status */}
          <div className="pd-form-group">
            <label>Status</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Plan or Course */}
          {form.dealType === "SUBSCRIPTION" && (
            <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Subscription Plan *</label>
              <select value={form.subscriptionPlanId} onChange={(e) => handlePlanChange(e.target.value)}>
                <option value="">— Select plan —</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {fmtAmt(p.price)}</option>)}
              </select>
            </div>
          )}

          {form.dealType === "COURSE" && (<>
            <div className="pd-form-group">
              <label>Course *</label>
              <select value={form.courseId} onChange={(e) => handleCourseChange(e.target.value)}>
                <option value="">— Select course —</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="pd-form-group">
              <label>Batch (optional)</label>
              <select value={form.batchId} onChange={(e) => set("batchId", e.target.value)} disabled={!form.courseId}>
                <option value="">— Select batch —</option>
                {batches.map((b) => <option key={b.id} value={b.id}>{b.batchName}</option>)}
              </select>
            </div>
          </>)}

          {/* Buyer */}
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Buyer (Registered User)</label>
            <UserSearchInput
              value={form.buyerUserId ? form.buyerName : ""}
              onChange={handleBuyerSelect}
            />
            {form.buyerUserId && (
              <div style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: 4 }}>User ID: #{form.buyerUserId}</div>
            )}
          </div>
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Buyer Name (if unregistered)</label>
            <input value={form.buyerName} onChange={(e) => set("buyerName", e.target.value)} placeholder="Full name"
              style={form.buyerUserId ? { background: "#f8fafc", color: "#64748b" } : {}} readOnly={!!form.buyerUserId} />
          </div>
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Buyer Phone (if unregistered)</label>
            <PhoneInput
              countryCodes={countryCodes}
              dialCode={form.buyerCountryCode}
              onDialCodeChange={(val) => set("buyerCountryCode", val)}
              mobileNumber={form.buyerPhone}
              onMobileNumberChange={(val) => set("buyerPhone", val)}
              placeholder="Buyer Mobile Number"
            />
          </div>

          {/* Pricing */}
          <div className="pd-form-group">
            <label>Original Price (₹) *</label>
            <input type="number" min="0" step="0.01" value={form.originalPrice} onChange={(e) => set("originalPrice", e.target.value)} placeholder="0.00" />
          </div>
          <div className="pd-form-group">
            <label>Discounted Price (₹) *</label>
            <input type="number" min="0" step="0.01" value={form.discountedPrice} onChange={(e) => set("discountedPrice", e.target.value)} placeholder="Actual price paid" />
          </div>
          <div className="pd-form-group">
            <label>Commission % *</label>
            <input type="number" min="0.01" max="100" step="0.01" value={form.commissionPercent} onChange={(e) => set("commissionPercent", e.target.value)} placeholder="e.g. 10" />
          </div>

          {/* Live commission preview */}
          <div className="pd-form-group" style={{ justifyContent: "flex-end" }}>
            <label>Commission Amount (auto)</label>
            <div style={{ padding: "8px 10px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, fontWeight: 700, color: liveCommission ? "#15803d" : "#94a3b8", fontSize: "0.95rem" }}>
              {liveCommission ? fmtAmt(liveCommission) : "—"}
            </div>
          </div>

          {/* Notes */}
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
              style={{ padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: "0.875rem", resize: "vertical", outline: "none", fontFamily: "inherit" }}
              placeholder="e.g. Referred via WhatsApp" />
          </div>
        </div>

        {err && <div className="pd-error">{err}</div>}
        <div className="pd-modal-actions">
          <button className="pd-btn pd-btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          <button className="pd-btn pd-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────
const CommissionDashboard = () => {
  const [activeTab,    setActiveTab]    = useState(0);
  const [rows,         setRows]         = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [toast,        setToast]        = useState(null);
  const [modal,        setModal]        = useState(null);   // null | "add" | rowObj
  const [confirm,      setConfirm]      = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [coordFilter,  setCoordFilter]  = useState("");

  const showToast = (msg, type = "success") => setToast({ msg, type });

  // Load active coordinators for dropdowns
  useEffect(() => {
    fetchCoordinators({ active: true }).then((r) => setCoordinators(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = {};
      if (statusFilter) params.status        = statusFilter;
      if (coordFilter)  params.coordinatorId = coordFilter;
      const res = await fetchCommissions(params);
      setRows(res.data || []);
    } catch { setError("Failed to load commissions."); }
    finally  { setLoading(false); }
  }, [statusFilter, coordFilter]);

  useEffect(() => { if (activeTab === 0) load(); }, [activeTab, load]);

  const afterSave = (msg) => { setModal(null); showToast(msg); load(); };

  const handleDelete = (row) => {
    setConfirm({
      message: `Delete this commission entry for "${row.coordinatorName}"? This cannot be undone.`,
      danger: true,
      action: async () => {
        try {
          await deleteCommission(row.id);
          setConfirm(null);
          showToast("Commission deleted.");
          load();
        } catch { showToast("Failed to delete.", "error"); setConfirm(null); }
      },
    });
  };

  return (
    <div className="pd-page">
      {toast   && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      {confirm && <ConfirmDialog message={confirm.message} danger={confirm.danger} onConfirm={confirm.action} onCancel={() => setConfirm(null)} />}
      {modal   && (
        <CommissionModal
          initial={modal === "add" ? null : modal}
          coordinators={coordinators}
          onClose={() => setModal(null)}
          onSuccess={afterSave}
        />
      )}

      <div className="pd-header">
        <h2 className="pd-title">Commissions</h2>
        {activeTab === 0 && (
          <button className="pd-btn pd-btn-primary" onClick={() => setModal("add")}>+ Add Commission</button>
        )}
      </div>

      {/* Tabs */}
      <div className="pd-tabs">
        {TABS.map((t, i) => (
          <button key={t} className={`pd-tab ${activeTab === i ? "pd-tab-active" : ""}`} onClick={() => setActiveTab(i)}>{t}</button>
        ))}
      </div>

      {activeTab === 1 ? (
        <SummaryTab />
      ) : (
        <>
          <div className="pd-filter-bar">
            <select className="pd-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="pd-select" value={coordFilter} onChange={(e) => setCoordFilter(e.target.value)}>
              <option value="">All Coordinators</option>
              {coordinators.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button className="pd-btn pd-btn-ghost pd-btn-sm" onClick={() => { setStatusFilter(""); setCoordFilter(""); }}>Clear</button>
          </div>

          {error && <div className="pd-error-bar">{error}</div>}

          <div className="pd-table-area">
            {loading ? (
              <div className="pd-loading">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="pd-empty">
                <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>💰</div>
                <div>No commissions found.</div>
              </div>
            ) : (
              <>
                <div className="pd-count">{rows.length} commission{rows.length !== 1 ? "s" : ""}</div>
                <div className="pd-table-wrap">
                  <table className="pd-table">
                    <thead>
                      <tr>
                        <th>Coordinator</th>
                        <th>Deal</th>
                        <th>Plan / Course</th>
                        <th>Buyer</th>
                        <th>Original</th>
                        <th>Discounted</th>
                        <th>%</th>
                        <th>Commission</th>
                        <th>Status</th>
                        <th>Paid At</th>
                        <th>Expense</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id} style={r.status === "CANCELLED" ? { opacity: 0.55 } : {}}>
                          <td>
                            <div className="pd-cell-bold">{r.coordinatorName}</div>
                            {r.coordinatorPhone && <div className="pd-cell-sub">{r.coordinatorPhone}</div>}
                          </td>
                          <td><span className="pd-badge pd-badge-indigo">{r.dealType}</span></td>
                          <td className="pd-cell-bold">
                            {r.subscriptionPlanName || r.courseName || "—"}
                            {r.batchName && <div className="pd-cell-sub">{r.batchName}</div>}
                          </td>
                          <td className="pd-cell-sub">
                            {r.buyerName || (r.buyerUserId ? `User #${r.buyerUserId}` : "—")}
                            {r.buyerPhone && <div>{r.buyerPhone}</div>}
                          </td>
                          <td className="pd-cell-sub">{fmtAmt(r.originalPrice)}</td>
                          <td className="pd-cell-amount">{fmtAmt(r.discountedPrice)}</td>
                          <td className="pd-cell-sub">{r.commissionPercent}%</td>
                          <td className="pd-cell-amount">{fmtAmt(r.commissionAmount)}</td>
                          <td><span className={`pd-badge ${STATUS_BADGE[r.status] || "pd-badge-gray"}`}>{r.status}</span></td>
                          <td className="pd-cell-sub">{fmtDate(r.paidAt)}</td>
                          <td>
                            {r.linkedExpenseId
                              ? <span className="pd-badge pd-badge-blue" style={{ cursor: "pointer" }} title={`Expense #${r.linkedExpenseId}`}>Exp #{r.linkedExpenseId}</span>
                              : <span className="pd-cell-sub">—</span>}
                          </td>
                          <td>
                            <div className="pd-cell-actions">
                              <button className="pd-btn pd-btn-ghost pd-btn-sm" onClick={() => setModal(r)}>✏️</button>
                              {r.status === "INITIATED" && (
                                <button className="pd-btn pd-btn-danger-ghost pd-btn-sm" onClick={() => handleDelete(r)}>🗑️</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CommissionDashboard;
